// One device's cold chain setup: Vehicle / Fixed location (+ its cold chain site), up to 4 cold points, copy.
import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Plus, Pencil, Archive, Copy, Loader2 } from 'lucide-react';
import { coldChain } from '@/lib/trackizy';
import { showToast } from '@/components/shared/Toast';
import { orgTimezone } from '@/features/staff/attendanceModel';
import ColdPointForm from './ColdPointForm';
import CopyColdPointsDialog from './CopyColdPointsDialog';
import { formatRange, splitNumbers } from './coldChainModel';

const smallBtn = 'inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800';

// Archiving is a recorded change: the reason lands in the point's version history, so it is required.
function ArchiveReasonDialog({ point, busy, onConfirm, onCancel }) {
  const { t } = useTranslation();
  const uid = useId();
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState(false);
  function submit(e) {
    e.preventDefault();
    if (!reason.trim()) { setMissing(true); return; }
    onConfirm(reason.trim());
  }
  return (
    <form role="alertdialog" aria-labelledby={`${uid}-title`} onSubmit={submit} noValidate
      className="space-y-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/30">
      <div id={`${uid}-title`} className="text-sm font-semibold text-slate-800 dark:text-slate-100">{t('coldChain.setup.archiveTitle', 'Archive this cold point?')}</div>
      <p className="text-xs text-slate-600 dark:text-slate-300"><bdi>{point.name}</bdi> — {t('coldChain.setup.archiveConfirm', 'Monitoring stops and the slot frees up. Its history stays in reports.')}</p>
      <label htmlFor={`${uid}-reason`} className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('coldChain.setup.archiveReason', 'Reason for archiving')} *</label>
      <textarea id={`${uid}-reason`} rows={2} maxLength={500} aria-required="true" value={reason} onChange={(e) => { setReason(e.target.value); setMissing(false); }}
        className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200" />
      {missing && <span role="alert" className="block text-xs text-rose-600">{t('coldChain.setup.archiveReasonRequired', 'Say why you are archiving this cold point')}</span>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className={smallBtn}>{t('coldChain.form.cancel', 'Cancel')}</button>
        <button type="submit" disabled={busy} className="rounded-xl bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-60">{t('coldChain.setup.archive', 'Archive')}</button>
      </div>
    </form>
  );
}

export default function DeviceColdPointsEditor({ deviceId, onChanged }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);   // null | 'new' | cold point
  const [busy, setBusy] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [archiving, setArchiving] = useState(null); // null | cold point awaiting a reason
  const [serverError, setServerError] = useState(null);   // { code, fields? } from the last failed save
  const [formGen, setFormGen] = useState(0);              // remounts the form on the reloaded row after a 409
  const tz = orgTimezone(useSelector((s) => s.org?.settings));
  const [sites, setSites] = useState(null);       // the org's live cold chain sites, loaded with the device
  const [newSite, setNewSite] = useState(null);   // null | the name being typed for "New site…"

  const sourceText = (src) => {
    const m = /^(eye|ble|wired):([1-4])$/.exec(src ?? '');
    if (!m) return t('coldChain.source.custom', 'Custom keys (expert)');
    const family = { eye: t('coldChain.source.eye', 'EYE Sensor'), ble: t('coldChain.source.ble', 'BLE temperature logger'), wired: t('coldChain.source.wired', 'Wired probe') }[m[1]];
    return `${family} ${m[2]}`;
  };

  const load = useCallback(async () => {
    try {
      setData(await coldChain.devicePoints(deviceId));
    } catch (e) {
      showToast(e?.message || t('coldChain.loadError', 'Could not load cold chain data'));
    }
  }, [deviceId, t]);

  useEffect(() => { setData(null); setEditing(null); load(); }, [load]);
  useEffect(() => {
    let cancelled = false;
    coldChain.sites().then((r) => { if (!cancelled) setSites(r?.sites ?? []); }).catch(() => { if (!cancelled) setSites([]); });
    return () => { cancelled = true; };
  }, [deviceId]);

  async function changed() {
    await load();
    onChanged?.();
  }

  function stopEditing() { setEditing(null); setServerError(null); }

  async function save(payload) {
    setBusy(true);
    setServerError(null);
    try {
      if (editing === 'new') await coldChain.createPoint({ ...payload, deviceId });
      else await coldChain.updatePoint(editing.id, payload);
      showToast(t('coldChain.setup.saved', 'Cold point saved'));
      setEditing(null);
      await changed();
    } catch (e) {
      if (e?.status === 409 && editing !== 'new') {
        // Someone saved first: reload this device's points and reopen the form on the row we now have to edit.
        showToast(t('coldChain.errors.versionConflict', 'Someone else changed this cold point — reloaded the latest settings'));
        setServerError({ code: 'version_conflict' });
        try {
          const fresh = await coldChain.devicePoints(deviceId);
          setData(fresh);
          const again = fresh?.points?.find((p) => p.id === editing.id);
          setEditing(again ?? null);
        } catch { /* the toast already explains; the stale form stays */ }
        setFormGen((n) => n + 1);
      } else if (e?.code === 'reason_required') {
        setServerError({ code: 'reason_required', fields: e.body?.fields ?? [] });
      } else {
        showToast(e?.code === 'max_cold_points'
          ? t('coldChain.errors.max', 'This device already has 4 cold points.')
          : (e?.message || t('coldChain.errors.save', 'Could not save')));
      }
    } finally {
      setBusy(false);
    }
  }

  // Archive, never delete: monitoring stops and the slot frees up; the history stays in reports.
  async function archive(point, reason) {
    setBusy(true);
    try {
      await coldChain.archivePoint(point.id, { reason });
      showToast(t('coldChain.setup.archived', 'Cold point archived'));
      setArchiving(null);
      await changed();
    } catch (e) {
      showToast(e?.message || t('coldChain.errors.save', 'Could not save'));
    } finally {
      setBusy(false);
    }
  }

  async function saveMode(mode, siteId) {
    // A fixed location needs a site: switch the picker on locally and save once one is picked.
    if (mode === 'site' && !siteId) {
      setData((d) => ({ ...d, device: { ...d.device, mode: 'site', siteId: null } }));
      return;
    }
    try {
      const device = await coldChain.setDeviceMode(deviceId, mode === 'site' ? { mode, siteId } : { mode });
      setData((d) => ({ ...d, device }));
      showToast(t('coldChain.setup.modeSaved', 'Saved'));
      onChanged?.();
    } catch (e) {
      showToast(e?.message || t('coldChain.errors.save', 'Could not save'));
    }
  }

  // "New site…": create it with just a name (place and notes can be added under Setup → Sites) and put the device in it.
  async function createAndAssign() {
    const name = (newSite ?? '').trim();
    if (!name) return;
    try {
      const site = await coldChain.createSite({ name });
      setSites((list) => [...(list ?? []), site]);
      setNewSite(null);
      await saveMode('site', site.id);
    } catch (e) {
      showToast(e?.message || t('coldChain.errors.save', 'Could not save'));
    }
  }

  if (!data) return <Loader2 size={16} className="animate-spin text-slate-400" />;
  const { device, points } = data;
  const full = points.length >= 4;

  return (
    <div className="space-y-4">
      <div className="space-y-2 rounded-2xl border border-slate-200 p-3 dark:border-slate-800">
        <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">{t('coldChain.setup.mode', 'Mounted on')}</span>
        <div className="flex gap-1.5">
          {['vehicle', 'site'].map((m) => (
            <button key={m} type="button" aria-pressed={device.mode === m} onClick={() => saveMode(m, device.siteId)}
              className={`rounded-xl px-3 py-1.5 text-xs font-semibold ${device.mode === m ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
              {m === 'vehicle' ? t('coldChain.setup.modeVehicle', 'Vehicle') : t('coldChain.setup.modeSite', 'Fixed location')}
            </button>
          ))}
        </div>
        {device.mode === 'site' && (
          <div className="space-y-1.5">
            <label htmlFor={`site-${deviceId}`} className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">{t('coldChain.setup.site', 'Site')}</label>
            <select id={`site-${deviceId}`} value={newSite !== null ? '__new' : (device.siteId ?? '')} disabled={!sites}
              onChange={(e) => {
                if (e.target.value === '__new') { setNewSite(''); return; }
                setNewSite(null);
                if (e.target.value) saveMode('site', e.target.value);
              }}
              className="block h-9 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
              <option value="">{t('coldChain.setup.pickSite', 'Pick a site…')}</option>
              {(sites ?? []).map((s) => <option key={s.id} value={s.id}>{s.poiName ? `${s.name} · ${s.poiName}` : s.name}</option>)}
              <option value="__new">{t('coldChain.setup.newSite', 'New site…')}</option>
            </select>
            {newSite !== null && (
              <div className="flex gap-2">
                <input aria-label={t('coldChain.siteSetup.name', 'Site name')} autoFocus maxLength={120} value={newSite} onChange={(e) => setNewSite(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createAndAssign(); } }}
                  placeholder={t('coldChain.siteSetup.namePlaceholder', 'e.g. Cold room 1')}
                  className="block h-9 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-800" />
                <button type="button" disabled={!newSite.trim()} onClick={createAndAssign}
                  className="rounded-xl bg-blue-500 px-3 text-xs font-semibold text-white disabled:opacity-50">{t('coldChain.setup.createSite', 'Create')}</button>
              </div>
            )}
          </div>
        )}
      </div>

      {points.length === 0 && editing === null && (
        <p className="text-sm text-slate-500">{t('coldChain.setup.none', 'No cold points on this device yet.')}</p>
      )}
      <ul className="space-y-2">
        {points.map((p) => (
          <li key={p.id} className="rounded-2xl border border-slate-200 p-3 dark:border-slate-800">
            {archiving?.id === p.id ? (
              <ArchiveReasonDialog point={p} busy={busy} onConfirm={(reason) => archive(p, reason)} onCancel={() => setArchiving(null)} />
            ) : editing?.id === p.id ? (
              <ColdPointForm key={`${p.id}-${formGen}`} initial={p} busy={busy} onSubmit={save} onCancel={stopEditing} tz={tz} serverError={serverError} />
            ) : (
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-100"><bdi>{p.name}</bdi>{!p.enabled && <span className="ms-2 text-xs text-slate-400">{t('coldChain.status.off', 'Off')}</span>}</div>
                  {p.locationDetail && <div className="text-xs text-slate-500"><bdi>{p.locationDetail}</bdi></div>}
                  {(p.calibratedOn || p.calibrationDueOn) && (
                    <div className="text-[11px] text-slate-400">
                      {p.calibratedOn && <span>{t('coldChain.setup.calibratedOn', 'Calibrated')} <bdi>{p.calibratedOn}</bdi></span>}
                      {p.calibratedOn && p.calibrationDueOn && ' · '}
                      {p.calibrationDueOn && <span>{t('coldChain.setup.calibrationDue', 'Due')} <bdi>{p.calibrationDueOn}</bdi></span>}
                    </div>
                  )}
                  <div className="text-xs text-slate-500">{sourceText(p.tempSource)} · <bdi>{formatRange(p.rangeMin, p.rangeMax)}</bdi></div>
                </div>
                <div className="flex gap-1">
                  <button type="button" className={smallBtn} onClick={() => { setServerError(null); setEditing(p); }}><Pencil size={13} />{t('coldChain.setup.edit', 'Edit')}</button>
                  <button type="button" className={`${smallBtn} text-rose-600`} onClick={() => setArchiving(p)}><Archive size={13} />{t('coldChain.setup.archive', 'Archive')}</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {editing === 'new' && (
        <div className="rounded-2xl border border-blue-200 p-3 dark:border-blue-900">
          <ColdPointForm busy={busy} onSubmit={save} onCancel={stopEditing} tz={tz} serverError={serverError} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={full || editing !== null} onClick={() => setEditing('new')}
          className="inline-flex items-center gap-1 rounded-xl bg-blue-500 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
          <Plus size={14} />{t('coldChain.setup.add', 'Add cold point')}
        </button>
        {points.length > 0 && (
          <button type="button" className={smallBtn} onClick={() => setCopyOpen(true)}><Copy size={13} />{t('coldChain.setup.copy', 'Copy to other vehicles')}</button>
        )}
        {full && (
          <span className="text-xs text-slate-500">
            {splitNumbers((o) => t('coldChain.setup.max', 'A device has up to {{n}} cold points.', o), { n: 4 }, (v, k) => <bdi key={k}>{v}</bdi>)}
          </span>
        )}
      </div>

      {copyOpen && <CopyColdPointsDialog fromDeviceId={deviceId} onClose={() => setCopyOpen(false)} onDone={onChanged} />}
    </div>
  );
}
